import * as queryString from 'querystring'

import React, { useState } from 'react'
import { Colors, NonIdealState } from '@blueprintjs/core'
import { Atom, useSelector } from 'klyva'
import { Box, getShownColumns, quarks, ErrorState, JsonDialog } from 'ui-components'
import { TaskT, TaskActionT, TaskActionTypeT } from 'ui-io/task-io/task-t'
import { AssignmentTypeT, SortOrderT } from 'ui-io/task-io'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { TaskSearchResultT } from 'ui-io/queries/tasks/tasks-query'
import useNewKlyvaAtom from 'ui-lib/use-new-klyva-atom'

// import ErrorState from '../error-state'
import GridTable from '../../grid-table'
// import JsonDialog from '../json-dialog'
import ModifyAssignmentDialog from './modify-assignment-dialog'
import SortButton from '../../sort-button'
import TaskRow from './task-row'
import { SelectAllTask } from '../task-cells/task-select-cell'
import {
  TaskColumnId,
  TaskColumnEntry,
  TaskColumnSettings,
  TaskColumnSettingsAtom,
  UTILITIES_COLUMN,
  TASK_SELECT_COLUMN,
  NormalTaskColumn,
  isNormalTaskColumn,
} from '../task-columns'
import { ContextMenuItem } from '../task-context-menu/task-row-context-menu'
import { TASK_CONTEXT_MENU_ITEMS } from '../task-context-menu/task-row-context-menu'
import makeTaskColumnSettings from './make-task-column-settings'
import TableActions from './table-actions'
import taskSorters, { TaskSortKey } from './task-sorters'
import { TaskValueKey } from '../column-values/value-extractors'
import {
  getEnabledColumnIdsFromStore,
  setUserColumnsFromStoreFactory,
} from '../../../utils/enabled-columns-store'
import { InboxViewT, isBulkView } from '../../../pages/tasks/inbox-views'
import { formatLocation, MatchT } from '../../../routes'
import EditTaskDialog from '../../../pages/task/edit-task-dialog'

type TaskTableHeadersProps = {
  shownColumns: TaskColumnEntry[]
  sortBy: (value: TaskValueKey) => void
  currentSortValueKey: TaskValueKey
  invertedSort: boolean
  tasks: TaskT[]
  onTasksSelect?: (task: TaskT[], selected: boolean) => void
  selectedTasks?: TaskT[]
  view?: InboxViewT
}
const TaskTableHeaders = ({
  shownColumns,
  sortBy,
  currentSortValueKey,
  invertedSort,
  tasks,
  onTasksSelect,
  selectedTasks,
  view,
}: TaskTableHeadersProps) => {
  return (
    <thead>
      <tr>
        {shownColumns.map(({ id, heading, sortKey }) =>
          id === UTILITIES_COLUMN ? (
            <th key={id}></th>
          ) : id === TASK_SELECT_COLUMN &&
            view === 'Approved' &&
            selectedTasks &&
            onTasksSelect ? (
            <th key={id}>
              <Box mt={8}>
                <SelectAllTask {...{ tasks, selectedTasks, onTasksSelect }} />
              </Box>
            </th>
          ) : (
            <th key={id}>
              <div className="header">
                <div>
                  {sortKey ? (
                    <SortButton
                      {...{
                        sortBy,
                        currentSortKey: currentSortValueKey,
                        invertedSort,
                        heading,
                        sortKey,
                      }}
                    />
                  ) : (
                    heading
                  )}
                </div>
              </div>
            </th>
          ),
        )}
      </tr>
    </thead>
  )
}

type TaskGridTableProps = {
  sortBy: (value: TaskValueKey) => void
  currentSortValueKey: TaskValueKey
  selectedTasks?: TaskT[]
  onTasksSelect?: (tasks: TaskT[], selected: boolean) => void
  invertedSort: boolean
  columnSettingsAtom: TaskColumnSettingsAtom
  onClick?: (task: TaskT) => void
  sortedTasks: TaskT[]
  contextMenuItems?: ContextMenuItem[]
  setJsonDialogTask?: (task: TaskT | undefined) => void
  jsonDialogTask?: TaskT
  selectedTaskId?: string
  columnForChildAutoOpening?: TaskColumnId
  setTaskAction?: (taskAction: TaskActionT | null) => void
  recentlyRequestedTaskId?: string
  view?: InboxViewT
}
export const TaskGridTable = (props: TaskGridTableProps) => {
  const {
    columnSettingsAtom,
    sortBy,
    selectedTasks,
    onTasksSelect,
    currentSortValueKey,
    invertedSort,
    onClick,
    sortedTasks,
    contextMenuItems,
    setJsonDialogTask,
    jsonDialogTask,
    selectedTaskId,
    columnForChildAutoOpening,
    setTaskAction,
    recentlyRequestedTaskId,
    view,
  } = props
  let shownColumns = useSelector(columnSettingsAtom, getShownColumns)
  shownColumns = [
    ...shownColumns,
    {
      id: '_UTILITY_MENU',
      label: '...',
      heading: '',
      sortKey: null,
      minWidthPx: 85,
    },
  ]

  if (!!view && isBulkView(view)) {
    shownColumns = [
      {
        id: '_TASK_SELECT',
        label: 'Select tasks',
        heading: '',
        sortKey: 'unsortable',
        minWidthPx: 40,
      },
      ...shownColumns,
    ]
  }
  const minWidthsPx = shownColumns.map(
    ({ minWidthPx }): number => minWidthPx ?? 0,
  )

  return (
    <Box style={{ maxWidth: '100%' }}>
      <GridTable $minWidthsPx={minWidthsPx}  interactive={!!onClick}>
        <TaskTableHeaders
          {...{
            shownColumns,
            sortBy,
            currentSortValueKey,
            invertedSort,
            tasks: sortedTasks,
            selectedTasks,
            onTasksSelect,
            view,
          }}
        />
        <tbody>
          {sortedTasks.map((task) => (
            <TaskRow
              recentlyAdded={recentlyRequestedTaskId === task.id}
              selectedTasks={selectedTasks}
              onTasksSelect={onTasksSelect}
              key={task.id}
              onClick={onClick}
              task={task}
              shownColumnIds={shownColumns.map(({ id }) => id)}
              contextMenuItems={contextMenuItems}
              onShowJson={() => {
                setJsonDialogTask && setJsonDialogTask(task)
              }}
              setTaskAction={(actionType: TaskActionTypeT) => {
                setTaskAction && setTaskAction({ task, actionType })
              }}
              highlight={task.id === jsonDialogTask?.id}
              dashboardHighlight={task.id === selectedTaskId}
              columnForChildAutoOpening={columnForChildAutoOpening}
            />
          ))}
        </tbody>
      </GridTable>
    </Box>
  )
}

type TasksTableProps = {
  tasks: TaskT[]
  selectedTaskId?: string
  selectedTasks?: TaskT[]
  onTasksSelect?: (task: TaskT[], selected: boolean) => void
  tableStoreId?: string
  error?: unknown
  onClick?: (task: TaskT) => void
  defaultColumns: NormalTaskColumn[]
  contextMenuItems?: ContextMenuItem[]
  defaultSort: TaskSortKey
  columnForChildAutoOpening?: TaskColumnId
  renderButtons: () => React.ReactElement | null
  assignmentType?: AssignmentTypeT
  recentlyRequestedTaskId?: string
  view: InboxViewT
  sortOrder: SortOrderT
  allUserList?: string[]
  taskResults: TaskSearchResultT | undefined
}

export const useSortTasks = ({
  defaultSort,
  tasks,
  sortOrder,
  Page,
}: {
  defaultSort: TaskSortKey
  tasks: TaskT[]
  sortOrder: SortOrderT
  Page?: MatchT
}) => {
  const [currentSortValueKey, setCurrentSortValueKey] =
    useState<TaskSortKey>(defaultSort)
  const [invertedSort, setInvertedSort] = useState(sortOrder === 'ASC')
  const history = useNavigate()
  const sortBy = (value: TaskSortKey): void => {
    const queryStringObj = queryString.parse(
      window.location.search.replace('?', ''),
    )
    if (currentSortValueKey === value) {
      setInvertedSort((value) => !value)
      queryStringObj.sortOrder = invertedSort ? 'DSC' : 'ASC'
    } else {
      setCurrentSortValueKey(value)
      setInvertedSort(true)
      queryStringObj.sortOrder = 'ASC'
    }
    if (Page === 'taskInbox') {
      history(
        formatLocation.taskInbox({
          ...queryStringObj,
          sortBy: value,
        }),
      )
    }
  }
  const sortedTasks = tasks.sort(taskSorters[currentSortValueKey](invertedSort))
  return {
    sortedTasks,
    currentSortValueKey,
    invertedSort,
    sortBy,
  }
}

const getToggleDisabledColumnsList = (view: InboxViewT) => {
  if (view === 'Pending' || view === 'Inbox') {
    return new Set<TaskColumnId>([
      'taskType',
      'creationTime',
      'companyName',
      'taskActions',
    ] as const)
  }
  return new Set<TaskColumnId>([
    'taskType',
    'companyName',
    'taskActions',
  ] as const)
}

const TasksTable = ({
  tasks,
  selectedTaskId,
  selectedTasks,
  onTasksSelect,
  error,
  onClick,
  defaultColumns,
  tableStoreId = 'tasks-table',
  contextMenuItems = [...TASK_CONTEXT_MENU_ITEMS],
  defaultSort,
  columnForChildAutoOpening,
  renderButtons,
  assignmentType,
  recentlyRequestedTaskId,
  view,
  sortOrder,
  allUserList,
  taskResults,
}: TasksTableProps) => {
  const { sortedTasks, currentSortValueKey, invertedSort, sortBy } =
    useSortTasks({
      defaultSort,
      tasks,
      sortOrder,
    })
  const [jsonDialogTask, setJsonDialogTask] = useState<TaskT | undefined>()
  const [taskAction, setTaskAction] = useState<TaskActionT | null>(null)

  const columnSettingsAtom: Atom<TaskColumnSettings> = useNewKlyvaAtom(() => {
    const savedColumns = getEnabledColumnIdsFromStore<NormalTaskColumn>(
      tableStoreId,
      isNormalTaskColumn,
    )
    return makeTaskColumnSettings(defaultColumns, savedColumns)
  })

  const togglingDisabledColumns = getToggleDisabledColumnsList(view)

  columnSettingsAtom.subscribe(setUserColumnsFromStoreFactory(tableStoreId))

  return (
    <>
      {error ? (
        <ErrorState error={error} />
      ) : (
        <>
          <Box justify="between" width="1">
            <TableActions
              {...{
                sortedTasks,
                columnSettingsAtom,
                view,
                togglingDisabledColumns,
              }}
            />
            {renderButtons()}
          </Box>

          {!taskResults ? null : (
            <PageInfoContainer>
              Page {taskResults.page ? taskResults.page + 1 : 1} of{' '}
              {taskResults.total_pages ? taskResults.total_pages : 1} |{' '}
              <span>{taskResults.hits.total}</span> total results
            </PageInfoContainer>
          )}

          {tasks.length === 0 ? (
            <NonIdealState
              icon="folder-open"
              title="No tasks found."
              description="Try again with a different search criteria or request a new task"
            />
          ) : (
            <TaskGridTable
              {...{
                recentlyRequestedTaskId,
                columnSettingsAtom,
                sortBy,
                currentSortValueKey,
                invertedSort,
                onClick,
                sortedTasks,
                contextMenuItems,
                setJsonDialogTask,
                jsonDialogTask,
                selectedTaskId,
                columnForChildAutoOpening,
                setTaskAction,
                selectedTasks,
                onTasksSelect,
                view,
              }}
            />
          )}
          <JsonDialog
            title={'JSON Inspector'}
            jsonData={jsonDialogTask || {}}
            isDialogOpen={!!jsonDialogTask}
            onDialogClose={() => setJsonDialogTask(undefined)}
          />
          {!!taskAction && taskAction.actionType !== 'EDIT' && (
            <ModifyAssignmentDialog
              {...{
                task: taskAction.task,
                actionType: taskAction.actionType,
                setTaskAction: () => {
                  setTaskAction(null)
                },
                assignmentType,
                allUserList,
              }}
            />
          )}
          {taskAction?.task && (
            <EditTaskDialog
              isOpen={taskAction.actionType === 'EDIT'}
              task={taskAction.task}
              onClose={() => {
                setTaskAction(null)
              }}
            />
          )}
        </>
      )}
    </>
  )
}

const PageInfoContainer = styled.div`
  font-size: ${quarks.fontSizes.small};
  font-weight: ${quarks.fontWeights.bold};
  text-transform: uppercase;
  margin-top: ${quarks.margins.small};
  text-align: right;

  & > span {
    color: ${Colors.BLUE5};
  }
`
export default TasksTable
